import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { SortableTableHeader, useSortableData } from "../components/ui/sortable-table";
import { CreateDeskModal } from "../components/desks//CreateDeskModal";
import { DeskDetailModal } from "../components/desks//DeskDetailModal";
import { Plus, Search, Users, Building2, Edit, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { deleteDesk, fetchAvailableDesks, fetchDesks } from "../services/crmServices/deskService";
import Swal from "sweetalert2";
import { getFullName, updateFirstLetterToUpperCase } from "../services/common/util";
import { useAuth } from "../hooks/useAuth";

export function Desks() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [desks, setDesks] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDesk, setSelectedDesk] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }
        initPage();
    }, []);

    const initPage = () => {
        setLoading(true);
        fetchAvailableDesks()
            .then((res) => {
                if (res?.data?.length) {
                    setDesks(res.data);
                } else {
                    setDesks([]);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Filter and sort using real desks
    const filteredDesks = desks.filter(
        (desk) =>
            (desk.name && desk.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (desk.id && desk.id.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const { sortedData, sortConfig, handleSort } = useSortableData(filteredDesks, "_id");

    const totalPages = Math.ceil(sortedData.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedDesks = sortedData.slice(startIndex, startIndex + pageSize);

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    // Refresh desks after create
    const handleCreateSuccess = () => {
        fetchDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        setShowCreateModal(false);
    };

    // Refresh desks after edit
    const handleEditSuccess = (updatedDesk) => {
        fetchDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        setShowEditModal(false);
        setSelectedDesk(null);
    };

    const handleViewDesk = (desk) => {
        setSelectedDesk(desk);
        setShowDetailModal(true);
    };

    const handleEditDesk = (desk) => {
        setSelectedDesk(desk);
        setShowEditModal(true);
    };

    const handleDelete = (_id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this team!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, keep it"
        }).then((result) => {
            if (result.isConfirmed) {
                deleteDesk({ _id }).then((res) => {
                    if (res.status !== 200) {
                        toast.error("Something went wrong while deleting the team.");
                        return;
                    } else {
                        toast.success("Desk deleted successfully");
                        initPage();
                    }
                });
            }
        });
    };

    const handleBack = () => {
        navigate("/");
    };

    if (loading) {
        return (
            <div className="w-full max-w-[1600px] mx-auto px-4">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[1600px] mx-auto px-4">
            <div className="mb-4">
                <div className="mb-3">
                    <h1 className="text-xl font-semibold text-gray-900">Teams Management</h1>
                    <p className="text-xs text-gray-600 mt-0.5">Manage teams and organization</p>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <Input
                            placeholder="Search teams..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 h-8 text-sm"
                        />
                    </div>
                    {user.permissions.desks.create && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                            Add Desk
                        </button>
                    )}
                </div>
            </div>

            {/* Compact Table View */}
            <div className="rounded border border-gray-200 overflow-x-auto bg-white">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="text-xs py-2">Actions</TableHead>
                            <SortableTableHeader sortKey="name" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2 min-w-32">
                                Name
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="floor" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2">
                                Floor
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="floorManager" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2 min-w-32">
                                Floor Manager
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2 min-w-32">
                                Manager
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2">
                                Agents
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort} className="text-xs py-2">
                                Leads
                            </SortableTableHeader>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="text-xs">
                        {paginatedDesks.map((desk) => (
                            <TableRow key={desk._id}>
                                <TableCell className="py-2">
                                    <div className="flex gap-1">
                                        {user.permissions.desks.update && (
                                            <button
                                                onClick={() => handleEditDesk(desk)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors"
                                            >
                                                <Edit className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                        {user.permissions.desks.delete && (
                                            <button
                                                onClick={() => handleDelete(desk._id)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-red-600 bg-white border border-red-300 hover:bg-red-50:bg-red-900/30 rounded transition-colors"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell
                                    className="font-medium py-2 cursor-pointer hover:text-blue-600 min-w-32"
                                    onClick={() => handleViewDesk(desk)}
                                >
                                    {desk.name}
                                </TableCell>
                                <TableCell className="py-2">
                                    {desk.floor ? updateFirstLetterToUpperCase(desk.floor) : "N/A"}
                                </TableCell>
                                <TableCell className="py-2 min-w-32">
                                    {desk.floorManager ? getFullName(desk.floorManager) : "-"}
                                </TableCell>
                                <TableCell className="py-2 min-w-32">
                                    {desk.manager ? getFullName(desk.manager) : "-"}
                                </TableCell>
                                <TableCell className="py-2">{desk.agents.length}</TableCell>
                                <TableCell className="py-2">{desk.leads.length}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Compact Pagination */}
            <div className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded mt-3">
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-600">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white text-gray-900"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-xs text-gray-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>

            {filteredDesks.length === 0 && (
                <div className="text-center py-8 bg-white border border-gray-200 rounded mt-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">No teams found</h3>
                    <p className="text-xs text-gray-600">Try adjusting your search criteria</p>
                </div>
            )}

            <CreateDeskModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
            />

            <DeskDetailModal
                open={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedDesk(null);
                }}
                desk={selectedDesk}
            />

            <CreateDeskModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedDesk(null);
                }}
                onSuccess={handleEditSuccess}
                desk={selectedDesk}
            />
        </div>
    );
}
